import {Separator} from 'platform/components';
import {Box, Heading, HStack, Show, Text, VStack} from 'platform/foundation';

import type {ReactNode} from 'react';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, type Nullish} from 'shared/web';

import {PriceBreakdown, type PriceBreakdownProps} from '../PriceBreakdown/PriceBreakdown';

export type SummaryRailProps = {
  title?: string;
  rows: {label: string; value: ReactNode | string | Nullish}[];
  price?: Pick<
    PriceBreakdownProps,
    | 'totals'
    | 'currency'
    | 'isPending'
    | 'canViewCost'
    | 'cost'
    | 'proposed'
    | 'vatRateLabel'
    | 'basis'
  >;
  footer?: ReactNode;
  isSticky?: boolean;
};

const t = (key: string, defaultValue: string) => i18n.t(key, {defaultValue});

function RailRow(props: {label: string; value: ReactNode | string | Nullish}) {
  const isEmpty = isNil(props.value) || props.value === '';
  return (
    <HStack justify="space-between" align="flex-start" spacing={3}>
      <Text size="small" color="secondary">
        {props.label}
      </Text>
      <Box minWidth={0}>
        {typeof props.value === 'string' || isEmpty ? (
          <Text size="small" alternative align="right" color={isEmpty ? 'tertiary' : 'primary'}>
            {isEmpty ? EMPTY_PLACEHOLDER : props.value}
          </Text>
        ) : (
          props.value
        )}
      </Box>
    </HStack>
  );
}

function RailPrice(props: {price?: SummaryRailProps['price']}) {
  if (!props.price) {
    return null;
  }
  return <PriceBreakdown {...props.price} variant="inline" />;
}

export function SummaryRail(props: SummaryRailProps) {
  return (
    <Box position={props.isSticky === false ? undefined : 'sticky'} top={4} width="100%">
      <Box
        backgroundColor="general.white"
        border="1px solid"
        borderColor="general.separator"
        borderRadius="large"
        padding={4}
      >
        <VStack spacing={3}>
          <Heading size={4}>
            {props.title ?? t('entity.newVehicle.labels.liveSummary', 'Live summary')}
          </Heading>
          <VStack spacing={2}>
            {props.rows.map((row) => (
              <RailRow key={row.label} label={row.label} value={row.value} />
            ))}
          </VStack>
          <Show when={props.price}>
            <Separator spacing={0} />
            <RailPrice price={props.price} />
          </Show>
          <Show when={props.footer}>
            <Separator spacing={0} />
            {props.footer}
          </Show>
        </VStack>
      </Box>
    </Box>
  );
}
